import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import DesignCard from "../components/DesignCard";
import EmptyState from "../components/EmptyState";
import SkeletonCard from "../components/SkeletonCard";
import LifestyleStrip from "../components/LifestyleStrip";
import { WatchHintChip } from "../components/WatchHintChip";
import { Search, Zap } from "lucide-react";

const TYPOLOGIES = [
  { key: "all",                     label: "All" },
  { key: "single_family",           label: "Single-Family" },
  { key: "duplex",                  label: "Duplex" },
  { key: "adu",                     label: "ADU" },
];

const LOT_WIDTHS = [
  { key: "all", label: "All widths" },
  { key: "30",  label: "30 ft Narrow" },
  { key: "40",  label: "40 ft Standard" },
  { key: "50",  label: "50 ft +" },
];

const NEIGHBORHOODS = [
  { key: "all",                     label: "All neighborhoods" },
  { key: "Near Eastside",           label: "Near Eastside" },
  { key: "Martindale-Brightwood",   label: "Martindale-Brightwood" },
];

const PRICE_CAPS = [
  { key: "all",    label: "No limit" },
  { key: "250000", label: "Under $250,000" },
  { key: "325000", label: "Under $325,000" },
];

const DEFAULT_FILTERS = {
  typology: "all",
  lot_width: "all",
  neighborhood: "all",
  max_price: "all",
  q: "",
  lifestyle: "",
  has_track_record: false,
};

// Mirror of backend lib/zoning.design_fits_parcel — runs on the
// already-fetched design list to grey out non-fitting cards once the
// user has paired the catalog with a parcel.
function designFitReason(design, fit) {
  const max_w = fit?.max_house_width_ft;
  const typologies = new Set(fit?.permitted_typologies || []);
  if (!max_w || typologies.size === 0) return "Parcel zoning data unavailable";
  if (design?.typology && !typologies.has(design.typology)) {
    return `This parcel doesn't permit ${String(design.typology).replace(/_/g, " ")} construction.`;
  }
  const parcelWidth = fit?.parcel_summary?.width_ft || 0;
  const needed = design?.min_lot_width || 0;
  if (needed && needed > parcelWidth) {
    return `This design needs a lot ≥ ${needed}' wide — your parcel is ${parcelWidth}' wide.`;
  }
  return "";
}

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const architectId = searchParams.get("architect") || null;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [designs, setDesigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [architectChip, setArchitectChip] = useState(null);
  // Parcel-pairing state (zoning-fit algorithm)
  const [parcelFit, setParcelFit] = useState(null);
  const gridRef = useRef(null);

  // Resolve the architect's name when filtering by ?architect=…
  useEffect(() => {
    if (!architectId) {
      setArchitectChip(null);
      return;
    }
    let alive = true;
    api
      .get(`/architects/${architectId}`)
      .then((r) => {
        if (alive) setArchitectChip({ id: architectId, firm: r.data.firm, name: r.data.name });
      })
      .catch(() => {
        if (alive) setArchitectChip({ id: architectId, firm: "this architect", name: "" });
      });
    return () => {
      alive = false;
    };
  }, [architectId]);

  function clearArchitectFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("architect");
    setSearchParams(next, { replace: true });
  }

  // Stats — load once.
  useEffect(() => {
    api.get("/marketplace/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Designs — refetch on filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const params = {};
    if (filters.typology !== "all") params.typology = filters.typology;
    if (filters.lot_width !== "all") params.min_lot_width = Number(filters.lot_width);
    if (filters.neighborhood !== "all") params.neighborhood = filters.neighborhood;
    if (filters.max_price !== "all") params.max_price = Number(filters.max_price);
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.lifestyle) params.lifestyle = filters.lifestyle;
    if (architectId) params.architect_id = architectId;
    api
      .get("/marketplace/designs", { params })
      .then((r) => {
        if (!cancelled) setDesigns(r.data);
      })
      .catch((e) => !cancelled && setErr(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, architectId]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters(DEFAULT_FILTERS);

  // Client-side "Has built track record" filter. Server fetch already
  // honours typology / price / etc, but the prior_builds count is a
  // derived field served on each design — cheaper to filter the
  // already-fetched list than to add a new backend query param.
  const visibleDesigns = filters.has_track_record
    ? designs.filter((d) => (d.prior_builds || []).length > 0)
    : designs;

  return (
    <section data-testid="marketplace-page" className="animate-fade-in-up">
      <Hero stats={stats} />

      {/* Lifestyle preset strip */}
      <div className="mt-12">
        <LifestyleStrip
          value={filters.lifestyle}
          onChange={(v) => setF("lifestyle", v)}
          gridRef={gridRef}
        />
      </div>

      {/* Filters + grid */}
      <div
        className="grid lg:grid-cols-[236px_minmax(0,1fr)] gap-12"
        ref={gridRef}
      >
        <FilterRail
          filters={filters}
          setF={setF}
          onReset={reset}
          parcelFit={parcelFit}
          setParcelFit={setParcelFit}
        />

        <div>
          {architectChip && (
            <ArchitectFilterChip
              chip={architectChip}
              onClear={clearArchitectFilter}
            />
          )}

          <SearchRow
            q={filters.q}
            onQ={(v) => setF("q", v)}
            loading={loading}
            count={visibleDesigns.length}
          />

          {parcelFit && (
            <ParcelFitSummary fit={parcelFit} designs={visibleDesigns} />
          )}

          {err ? (
            <EmptyState
              title="Could not load the catalog."
              body="Confirm the backend is reachable, then try again."
              actionLabel="Retry"
              onAction={() => setFilters({ ...filters })}
            />
          ) : loading ? (
            <div
              className="grid md:grid-cols-2 xl:grid-cols-3"
              style={{ gap: 48, marginBottom: 64 }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : visibleDesigns.length === 0 ? (
            <EmptyState actionLabel="Reset filters" onAction={reset} />
          ) : (
            <div
              className="grid md:grid-cols-2 xl:grid-cols-3"
              style={{ gap: 48, marginBottom: 64 }}
              data-testid="design-grid"
            >
              {visibleDesigns.map((d) => {
                const reason = parcelFit
                  ? designFitReason(d, parcelFit)
                  : "";
                const fits = !reason;
                return (
                  <div
                    key={d.id}
                    data-testid={`design-card-frame-${d.id}`}
                    style={{
                      position: "relative",
                      opacity: !fits ? 0.42 : 1,
                      filter: !fits ? "grayscale(0.6)" : "none",
                      transition: "opacity 180ms ease",
                    }}
                  >
                    <DesignCard design={d} />
                    {!fits && (
                      <div
                        data-testid={`design-card-doesnt-fit-${d.id}`}
                        style={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          padding: "5px 10px",
                          background: "rgba(162,59,46,0.92)",
                          color: "#FBFAFD",
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          boxShadow: "0 2px 8px rgba(27,31,75,0.18)",
                          maxWidth: 220,
                          lineHeight: 1.35,
                        }}
                        title={reason}
                      >
                        Doesn&apos;t fit your parcel
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Hero ─────────────────────────── */

function Hero({ stats }) {
  return (
    <div
      className="grid lg:grid-cols-[1.35fr_1fr] gap-14 items-end"
      style={{ paddingBottom: 8 }}
    >
      <div className="max-w-[760px]">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Pre-reviewed housing database
          </span>
        </div>
        <h1
          className="mt-5"
          style={{
            fontSize: "clamp(40px, 5.6vw, 62px)",
            fontWeight: 500,
            lineHeight: 1.04,
            maxWidth: "min(1000px, 78vw)",
          }}
        >
          Find a home that{" "}
          <span style={{ fontStyle: "italic", color: "#8F3C23" }}>belongs</span>{" "}
          on your block.
        </h1>
        <p
          className="mt-5 max-w-[680px]"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 19,
            lineHeight: 1.55,
          }}
        >
          Architect-designed, pre-reviewed for code and zoning — a curated catalog
          built for Indianapolis infill.
        </p>

        <span
          aria-hidden
          style={{
            display: "block",
            width: 80,
            height: 2,
            background: "#B14E2F",
            marginTop: 36,
            marginBottom: 22,
          }}
        />
        <HeroStats stats={stats} />
      </div>

      {/* Right-rail editorial dateline (matches Landing) */}
      <aside
        className="hidden lg:block"
        style={{
          borderLeft: "1px solid #D9D8E4",
          paddingLeft: 32,
          paddingBottom: 8,
        }}
        aria-hidden
      >
        <div className="wn-label">Volume 01 · Catalog</div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 17,
            color: "#1B1F4B",
            lineHeight: 1.35,
          }}
        >
          A curated set of
          <br />
          pre-reviewed homes,
          <br />
          updated quarterly.
        </div>
        <p
          className="mt-5"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            lineHeight: 1.55,
            maxWidth: 260,
          }}
        >
          Every design has been vetted against Indianapolis zoning and the city
          building code. <span style={{ color: "#4F6149", whiteSpace: "nowrap" }}>
            10-Day Path
          </span>{" "}
          designates entries eligible for accelerated permitting.
        </p>
      </aside>
    </div>
  );
}

function HeroStats({ stats }) {
  const cells = [
    { value: stats?.approved_designs, label: "Designs" },
    { value: stats?.architects_verified, label: "Architects" },
  ];
  return (
    <div className="flex items-stretch" data-testid="hero-stats">
      {cells.map((c, i) => (
        <div
          key={c.label}
          style={{
            paddingRight: 36,
            paddingLeft: i === 0 ? 0 : 36,
            borderLeft: i === 0 ? "none" : "1px solid #D9D8E4",
          }}
        >
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 28,
              color: "#1B1F4B",
              lineHeight: 1,
            }}
          >
            {c.value == null ? "—" : c.value}
          </div>
          <div className="wn-label mt-2">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────── Search row ─────────────────────────── */

function SearchRow({ q, onQ, loading, count }) {
  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: "minmax(0, 420px) 1fr auto",
        columnGap: 24,
        rowGap: 12,
        marginBottom: 56,
      }}
    >
      <div style={{ position: "relative" }}>
        <Search
          size={16}
          color="#6C6B85"
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
        <input
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="Search designs, styles, or architects."
          className="wn-input"
          style={{ paddingLeft: 36, width: "100%" }}
          data-testid="design-search"
        />
      </div>

      <div
        style={{ color: "#6C6B85", fontSize: 14 }}
        data-testid="result-count"
      >
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
          }}
        >
          Showing{" "}
        </span>
        <strong
          className="tabular-nums"
          style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            color: "#1B1F4B",
          }}
        >
          {loading ? "…" : count}
        </strong>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
          }}
        >
          {" "}approved {count === 1 ? "design" : "designs"}
        </span>
      </div>

      <Link
        to="/registry"
        className="wn-btn wn-btn-tertiary"
        style={{ height: "auto", padding: 0 }}
        data-testid="browse-by-typology"
      >
        Browse by typology →
      </Link>
    </div>
  );
}

/* ─────────────────────────── Architect filter chip ─────────────────────────── */

function ArchitectFilterChip({ chip, onClear }) {
  return (
    <div
      data-testid="architect-filter-chip"
      className="flex flex-wrap items-center gap-3 mb-7"
      style={{
        padding: "10px 0",
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
      }}
    >
      <span className="wn-label" style={{ color: "#3A5563" }}>
        Filtered to architect
      </span>
      <span style={{ color: "#1B1F4B", fontWeight: 700, fontSize: 14 }}>
        {chip.firm}
      </span>
      <Link
        to={`/architects/${chip.id}`}
        data-testid="architect-filter-profile-link"
        style={{
          color: "#1B1F4B",
          fontSize: 12.5,
          textDecoration: "underline",
          textUnderlineOffset: 3,
        }}
      >
        View profile
      </Link>
      <button
        type="button"
        onClick={onClear}
        className="wn-btn wn-btn-tertiary"
        data-testid="architect-filter-clear"
        style={{ fontSize: 12, marginLeft: "auto" }}
      >
        Clear filter
      </button>
    </div>
  );
}

/* ─────────────────────────── Filter rail ─────────────────────────── */

function FilterRail({ filters, setF, onReset, parcelFit, setParcelFit }) {
  return (
    <aside data-testid="filter-rail">
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 19,
          color: "#1B1F4B",
        }}
      >
        Refine
      </div>
      <div
        style={{
          color: "#6C6B85",
          fontSize: 13,
          marginTop: 4,
          lineHeight: 1.5,
        }}
      >
        Narrow the catalog to your parcel and needs.
      </div>

      <ParcelPairBlock value={parcelFit} onChange={setParcelFit} />

      {!parcelFit && (
        <FilterGroup label="Lot width">
          <select
            value={filters.lot_width}
            onChange={(e) => setF("lot_width", e.target.value)}
            className="wn-input"
            data-testid="filter-lot-width"
          >
            {LOT_WIDTHS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </FilterGroup>
      )}

      <FilterGroup label="Typology">
        <div className="flex flex-wrap gap-2" data-testid="filter-typology">
          {TYPOLOGIES.map((t) => {
            const active = filters.typology === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setF("typology", t.key)}
                data-testid={`typology-chip-${t.key}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: `1px solid ${active ? "#B14E2F" : "#D9D8E4"}`,
                  backgroundColor: active ? "#B14E2F" : "transparent",
                  color: active ? "#FBFAFD" : "#1B1F4B",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 160ms ease",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      <FilterGroup label="Neighborhood">
        <select
          value={filters.neighborhood}
          onChange={(e) => setF("neighborhood", e.target.value)}
          className="wn-input"
          data-testid="filter-neighborhood"
        >
          {NEIGHBORHOODS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup label="Max price (2026)">
        <select
          value={filters.max_price}
          onChange={(e) => setF("max_price", e.target.value)}
          className="wn-input"
          data-testid="filter-max-price"
        >
          {PRICE_CAPS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </FilterGroup>

      {/* "Has built track record" toggle — collapses the catalog to
          plans that have been carried through construction at least
          once (prior_builds.length > 0). Sage palette so it threads
          the same evidence-anchored line that runs through the
          DesignCard "N BUILT" pill, the median-of-actuals price row,
          the design-detail FactSheet adjustment, and the architect
          profile track-record strip. */}
      <div className="mt-7" data-testid="filter-track-record-group">
        <label
          htmlFor="filter-track-record"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            cursor: "pointer",
            padding: "10px 12px",
            borderRadius: 4,
            border: `1px solid ${
              filters.has_track_record ? "#B8BFA0" : "#D9D8E4"
            }`,
            background: filters.has_track_record ? "#F4F2EC" : "transparent",
            transition: "all 160ms ease",
          }}
        >
          <input
            id="filter-track-record"
            type="checkbox"
            checked={filters.has_track_record}
            onChange={(e) => setF("has_track_record", e.target.checked)}
            data-testid="filter-track-record"
            style={{
              width: 16,
              height: 16,
              marginTop: 2,
              accentColor: "#4F6149",
              cursor: "pointer",
              flex: "0 0 auto",
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              className="wn-label"
              style={{
                color: filters.has_track_record ? "#3A4A35" : "#1B1F4B",
                letterSpacing: "0.08em",
              }}
            >
              Built track record
            </div>
            <div
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 12,
                lineHeight: 1.45,
                marginTop: 4,
              }}
            >
              Only show plans that have been carried through construction
              at least once.
            </div>
          </div>
        </label>
      </div>

      <button
        onClick={onReset}
        className="wn-btn wn-btn-tertiary mt-7"
        style={{ height: "auto", padding: 0 }}
        data-testid="filter-reset"
      >
        Reset filters
      </button>

      {/* De-boxed 10-Day Path note — hairline top rule, no card */}
      <div
        className="mt-10 pt-5"
        style={{ borderTop: "1px solid #D9D8E4" }}
        data-testid="ten-day-callout"
      >
        <div
          className="inline-flex items-center"
          style={{
            color: "#4F6149",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 11.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            gap: 6,
          }}
        >
          <Zap size={12} strokeWidth={2.5} />
          10-Day Path
        </div>
        <div
          style={{
            color: "#6C6B85",
            fontSize: 12.5,
            marginTop: 6,
            lineHeight: 1.6,
            maxWidth: 220,
          }}
        >
          Pre-reviewed designs eligible for accelerated permitting on
          conforming parcels.
        </div>
      </div>

      {/* "What is pre-reviewed housing?" — a single-tile hint into
          the Civic literacy track in the Reference Book. Lives below
          the 10-Day Path note so the filter rail closes on a public-
          good signal rather than a CTA. */}
      <div
        className="mt-8 pt-5"
        style={{ borderTop: "1px solid #D9D8E4" }}
        data-testid="filter-watch-hint-wrap"
      >
        <div
          className="wn-label"
          style={{ color: "#3A4A35", letterSpacing: "0.08em" }}
        >
          New to this?
        </div>
        <div className="mt-2.5" style={{ maxWidth: 240 }}>
          <WatchHintChip
            slug="what-is-pre-reviewed"
            variant="compact"
            testid="filter-watch-hint"
          />
        </div>
      </div>
    </aside>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mt-7">
      <div className="wn-label" style={{ color: "#6C6B85" }}>
        {label}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// ParcelPairBlock — top-of-sidebar input that pairs the catalog to a
// specific parcel via the zoning-fit algorithm. Shows an autocomplete
// when empty, a "fit" summary card once paired.
// ───────────────────────────────────────────────────────────────────
function ParcelPairBlock({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(false);
  const [cornerOverride, setCornerOverride] = useState(false);

  useEffect(() => {
    if (value) return;
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/marketplace/parcels-search?q=${encodeURIComponent(q.trim())}`,
        );
        if (alive) setResults(r.data || []);
      } catch {
        if (alive) setResults([]);
      }
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [q, value]);

  async function pickParcel(parcelId, corner = false) {
    setBusy(true);
    try {
      const r = await api.get(
        `/marketplace/zoning-fit?parcel_id=${encodeURIComponent(parcelId)}`
        + (corner ? "&is_corner=true" : ""),
      );
      onChange(r.data);
      setQ("");
      setOpen(false);
      setResults([]);
      setCornerOverride(corner);
    } finally {
      setBusy(false);
    }
  }

  async function toggleCorner() {
    if (!value) return;
    await pickParcel(value.parcel_summary.id, !cornerOverride);
  }

  function clearPairing() {
    onChange(null);
    setCornerOverride(false);
    setQ("");
    setResults([]);
    setWhy(false);
  }

  if (!value) {
    return (
      <div className="mt-5" data-testid="parcel-pair-block">
        <div
          className="wn-label"
          style={{ color: "#8F3C23", letterSpacing: "0.08em" }}
        >
          Pair with a parcel
        </div>
        <div style={{ fontSize: 12, color: "#6C6B85", marginTop: 2, lineHeight: 1.5 }}>
          Enter an address or parcel number to filter the catalog to
          only designs that fit your lot under Indianapolis zoning.
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="1812 Brookside Ave  ·  or 1015523"
          className="wn-input mt-2"
          data-testid="parcel-pair-input"
        />
        {open && results.length > 0 && (
          <div
            data-testid="parcel-pair-results"
            style={{
              marginTop: 6,
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              background: "#FBFAFD",
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickParcel(p.id)}
                disabled={busy}
                data-testid={`parcel-pair-pick-${p.id}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  background: "transparent",
                  border: 0,
                  borderBottom: "1px solid #ECEBF1",
                  cursor: busy ? "wait" : "pointer",
                }}
              >
                <div style={{ color: "#1B1F4B", fontWeight: 600, fontSize: 12.5 }}>
                  {p.address}
                </div>
                <div style={{ color: "#6C6B85", fontSize: 11, marginTop: 2 }}>
                  {p.parcel_id} · {p.zoning || "—"} · {p.width_ft || "?"}′ wide
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const s = value.parcel_summary || {};
  const permitted = value.permitted_types || [];
  return (
    <div
      className="mt-5"
      data-testid="parcel-pair-card"
      style={{
        border: "1px solid #B14E2F",
        borderRadius: 6,
        background: "#FFF6EA",
        padding: "12px 14px",
      }}
    >
      <div className="flex items-start" style={{ gap: 10 }}>
        <span
          aria-hidden
          style={{
            width: 18, height: 2, background: "#B14E2F", marginTop: 7,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Paired with parcel
          </div>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500, fontSize: 14, color: "#1B1F4B",
              marginTop: 2, lineHeight: 1.3,
            }}
          >
            {s.address || s.id}
          </div>
          <div style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 4 }}>
            {s.zoning_district || "—"} · {s.width_ft ? `${s.width_ft}′` : "?"}{" "}
            wide · {s.area_sqft ? `${Number(s.area_sqft).toLocaleString()} sqft` : "?"}
          </div>
          <div
            className="tabular-nums"
            style={{
              marginTop: 6, fontSize: 12.5, color: "#1B1F4B",
              fontWeight: 600,
            }}
            data-testid="parcel-pair-max-width"
          >
            Max house width{" "}
            <span style={{ color: "#8F3C23", fontSize: 16 }}>
              {value.max_house_width_ft
                ? `${value.max_house_width_ft}′`
                : "—"}
            </span>
          </div>
          <div className="flex items-center mt-2" style={{ gap: 12 }}>
            <label
              className="flex items-center"
              style={{
                gap: 6, fontSize: 11.5, color: "#6C6B85", cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={cornerOverride}
                onChange={toggleCorner}
                data-testid="parcel-pair-corner-toggle"
              />
              Corner lot
            </label>
            <button
              type="button"
              onClick={() => setWhy((w) => !w)}
              data-testid="parcel-pair-why"
              style={{
                background: "transparent", border: 0, padding: 0,
                cursor: "pointer", color: "#5B6196", fontSize: 11.5,
                fontWeight: 700, letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {why ? "Hide" : "Why these designs?"}
            </button>
            <button
              type="button"
              onClick={clearPairing}
              data-testid="parcel-pair-clear"
              style={{
                background: "transparent", border: 0, padding: 0,
                cursor: "pointer", color: "#A23B2E", fontSize: 11.5,
                fontWeight: 700, letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      {why && (
        <div
          data-testid="parcel-pair-explain"
          style={{
            marginTop: 12, paddingTop: 12,
            borderTop: "1px dashed rgba(177,78,47,0.32)",
          }}
        >
          {(value.notes || []).map((n, i) => (
            <p
              key={i}
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic", fontSize: 12, color: "#272636",
                lineHeight: 1.55, margin: "0 0 6px",
              }}
            >
              {n}
            </p>
          ))}
          {permitted.length > 0 && (
            <>
              <div
                className="wn-label mt-2"
                style={{ color: "#8F3C23", fontSize: 9.5 }}
              >
                Permitted building types
              </div>
              <ul
                style={{
                  margin: "4px 0 0", padding: 0, listStyle: "none",
                }}
              >
                {permitted.map((p) => (
                  <li
                    key={p.key}
                    style={{
                      fontSize: 11.5, color: "#1B1F4B", lineHeight: 1.45,
                      padding: "3px 0",
                      borderBottom: "1px solid rgba(177,78,47,0.18)",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{p.label}</span>{" "}
                    — {p.side}′ side ·{" "}
                    {p.max_house_width_ft
                      ? `max ${p.max_house_width_ft}′ wide`
                      : "—"}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// ParcelFitSummary — sits between the SearchRow and the design grid
// whenever a parcel is paired. Tells the user how many designs in the
// (already-filtered) catalog actually fit their lot, vs how many are
// being shown but greyed-out, plus a one-line zoning footnote.
// ───────────────────────────────────────────────────────────────────
function ParcelFitSummary({ fit, designs }) {
  const fitCount = designs.filter((d) => !designFitReason(d, fit)).length;
  const greyCount = designs.length - fitCount;
  const addr = fit?.parcel_summary?.address || fit?.parcel_summary?.id;
  const zoning = fit?.parcel_summary?.zoning_district;
  const width = fit?.parcel_summary?.width_ft;
  return (
    <div
      data-testid="parcel-fit-summary"
      style={{
        marginTop: -10,
        marginBottom: 24,
        padding: "10px 14px",
        border: "1px solid rgba(177,78,47,0.32)",
        borderRadius: 4,
        background: "rgba(177,78,47,0.04)",
        display: "flex",
        flexWrap: "wrap",
        gap: 14,
        alignItems: "baseline",
      }}
    >
      <span className="wn-label" style={{ color: "#8F3C23" }}>
        Paired
      </span>
      <span
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          color: "#1B1F4B",
          fontSize: 13,
          fontStyle: "italic",
        }}
      >
        {addr}
        {zoning && width ? ` · ${zoning} · ${width}′ wide` : ""}
      </span>
      <span style={{ flex: 1 }} />
      {fitCount > 0 ? (
        <span
          className="tabular-nums"
          data-testid="parcel-fit-summary-counts"
          style={{
            fontSize: 13,
            color: "#1B1F4B",
            fontWeight: 700,
          }}
        >
          {fitCount} {fitCount === 1 ? "design fits" : "designs fit"} ·{" "}
          <span style={{ color: "#8F3C23" }}>{greyCount} don&apos;t</span>
        </span>
      ) : (
        <span
          data-testid="parcel-fit-summary-empty"
          style={{
            fontSize: 12.5,
            color: "#8F3C23",
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            lineHeight: 1.5,
            maxWidth: 480,
          }}
        >
          No designs in today&apos;s catalog fit this parcel under
          Indianapolis zoning. Reach out to an architect — many will
          adapt an existing plan or design from scratch for tight lots.
        </span>
      )}
    </div>
  );
}

